import { useState, useCallback } from 'react';

/**
 * Hook for managing document selection state
 * Supports two modes:
 * - 'object': Stores full document objects in selection array
 * - 'key': Stores only keys (e.g., 'id' or 'filename') in selection array
 *
 * @param {Object} options Configuration options
 * @param {Array} options.initialSelected Initial selection array
 * @param {('object'|'key')} options.mode Selection storage mode (default: 'object')
 * @param {string} options.keyName Key name to use in 'key' mode (default: 'id')
 * @param {Function} options.getKeyFn Custom function to extract key from document
 * @returns {Object} Selection state and functions
 */
export function useDocumentSelection({
  initialSelected = [],
  mode = 'object',
  keyName = 'id',
  getKeyFn,
} = {}) {
  const [selected, setSelected] = useState(initialSelected);

  /**
   * Extract the identifier from a document based on mode
   */
  const getIdentifier = useCallback(
    (document) => {
      if (mode === 'key') {
        if (getKeyFn) {
          return getKeyFn(document);
        }
        return document[keyName] || document.attachments?.[0];
      }
      return document.id;
    },
    [mode, keyName, getKeyFn],
  );

  /**
   * Toggle document selection (add if not present, remove if present)
   */
  const toggleDocument = useCallback(
    (document) => {
      const identifier = getIdentifier(document);

      setSelected((prev) => {
        const isSelected = prev.some((item) => {
          const itemId = mode === 'object' ? item.id : item;
          return itemId === identifier;
        });

        if (isSelected) {
          // Remove from selection
          return prev.filter((item) => {
            const itemId = mode === 'object' ? item.id : item;
            return itemId !== identifier;
          });
        } else {
          // Add to selection
          const valueToAdd = mode === 'object' ? document : identifier;
          return [...prev, valueToAdd];
        }
      });
    },
    [mode, getIdentifier],
  );

  /**
   * Check if a document is selected
   */
  const isDocumentSelected = useCallback(
    (document) => {
      const identifier = getIdentifier(document);
      return selected.some((item) => {
        const itemId = mode === 'object' ? item.id : item;
        return itemId === identifier;
      });
    },
    [selected, mode, getIdentifier],
  );

  /**
   * Get the index of a document in the selection array
   * Returns -1 if not found
   */
  const getDocumentIndex = useCallback(
    (document) => {
      const identifier = getIdentifier(document);
      return selected.findIndex((item) => {
        const itemId = mode === 'object' ? item.id : item;
        return itemId === identifier;
      });
    },
    [selected, mode, getIdentifier],
  );

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelected([]);
  }, []);

  /**
   * Select multiple documents at once
   */
  const selectMultiple = useCallback(
    (documents) => {
      const newItems = documents.map((doc) => (mode === 'object' ? doc : getIdentifier(doc)));
      setSelected((prev) => [...prev, ...newItems]);
    },
    [mode, getIdentifier],
  );

  return {
    selected,
    setSelected,
    toggleDocument,
    isDocumentSelected,
    getDocumentIndex,
    clearSelection,
    selectMultiple,
  };
}

export default useDocumentSelection;
